import TextField from '@mui/material/TextField'
import React, {FC, useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {
  ChatUserType,
  GetDialogs,
  GetDialogsSearch,
  setFilterDialogList,
  useDialogsCursor,
  useDialogsList,
  useDialogsSearch,
  useDialogsSearchCursor,
  useDialogsSearchList,
  useDialogsTotalCount
} from 'entities/messages'
import {SetSetting} from 'entities/settings'
import {setSettingUser, useSound} from 'entities/user'
import {useDebounce, useVirtualScroll} from 'shared/hooks'
import {countriesMap, mediaVariants} from 'shared/lib'
import {displayCount} from 'shared/lib/entity'
import {Avatar} from 'shared/ui/avatar'
import {Mute, PinActive, Sound} from 'shared/ui/icons'
import './searchInput.scss'
import {Loader} from 'shared/ui/loader'
import styles from './styles.module.scss'

export const MyContacts = () => {
  const dispatch = useDispatch()
  const isSound = useSound()
  const totalCount = useDialogsTotalCount()

  const changeSound = () => {
    SetSetting('isAudioEnabled', !isSound)
      .then(() => dispatch(setSettingUser({key: 'isAudioEnabled', value: !isSound})))
      .catch(console.error)
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <span className={styles.titleText}>My Contacts</span>
          <span className={styles.countText}>{totalCount}</span>
        </div>
        <div
          className={styles.muteIcon}
          onClick={changeSound}
        >
          {isSound ? <Sound /> : <Mute />}
        </div>
      </div>
      <SearchContacts />
      <MyContactsList />
    </div>
  )
}

const MyContactsList = () => {
  const dispatch = useDispatch()
  const ref = useRef<HTMLDivElement>(null)
  const search = useDialogsSearch()
  const cursor = useDialogsCursor()
  const cursorSearch = useDialogsSearchCursor()
  const dialogsList = useDialogsList()
  const dialogsSearchList = useDialogsSearchList()

  const {refetch: loadDialogs, isError: isErrorDialogs, isFetching: isFetchingDialogs} = GetDialogs(dispatch)
  const {refetch: loadSearch, isError: isErrorSearch, isFetching: isFetchingSearch} = GetDialogsSearch(dispatch)

  const list = useMemo(() => search ? dialogsSearchList : dialogsList, [search, dialogsSearchList, dialogsList])
  const isFetching = useMemo(() => search ? isFetchingSearch : isFetchingDialogs, [search, isFetchingSearch, isFetchingDialogs])
  const isError = useMemo(() => search ? isErrorSearch : isErrorDialogs, [search, isErrorSearch, isErrorDialogs])

  useVirtualScroll({ref, isError: isErrorDialogs, isFetching: isFetchingDialogs, load: loadDialogs, cursor})
  useVirtualScroll({ref, isError: isErrorSearch, isFetching: isFetchingSearch, load: loadSearch, cursor: cursorSearch, skip: !search})

  return (
    <div className={`${styles.content} ${styles.customScroll}`} ref={ref}>
      {list.map((dialog) => (
        <ContactsItem
          key={dialog.interlocutor.id}
          idUser={dialog.interlocutor.id}
          name={dialog.interlocutor.name}
          avatarUrl={dialog.interlocutor.avatarUrl}
          pin={dialog.isPinned}
          online={dialog.interlocutor.isOnline}
          user={dialog.interlocutor}
          unreadCount={dialog.unreadCount}
        />
      ))}
      {!(list.length || isFetching) ? <div className={styles.emptyState}>
        You haven't added people<br />to your contacts yet
      </div> : null}
      {isFetching ? <div className={styles.loader}><Loader /></div> : null}
      {isError ? <div>Error</div> : null}
    </div>
  )
}

type ProfileItemProps = {
  idUser: string,
  avatarUrl?: string,
  name: string,
  pin: boolean,
  online?: boolean,
  unreadCount?: number,
  user: ChatUserType
}

const ContactsItem: FC<ProfileItemProps> = ({pin, online, unreadCount, name, avatarUrl, idUser, user}) => {
  const navigate = useNavigate()

  const handleClick = () => navigate(`/messages/${idUser}`)

  const onOpenProfile = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    event.stopPropagation()
    navigate(`/profile/${idUser}`)
  }

  return (
    <div className={styles.profileItem} onClick={handleClick}>
      <div>
        <Avatar
          src={avatarUrl + mediaVariants.square64}
          isOnline={online}
          onlineClass={styles.online}
          alt={name}
        />
      </div>
      <div className={styles.profileInfo}>
        <div className={styles.name}>
          <span className={styles.nameText} onClick={onOpenProfile}>{name}</span>
          {pin ? <div className={styles.pin}> <PinActive /></div>: null}
        </div>
        <div className={styles.location}>
          <span
            className={styles.text}>{user.country ? countriesMap[user.country] : ''}{user.city ? ', ' + user.city : ''}</span>
          {unreadCount ? <div className={styles.unread}>{displayCount(unreadCount)}</div> : null}
        </div>
      </div>
    </div>
  )
}

export const SearchContacts = () => {
  const dispatch = useDispatch()
  const [value, setValue] = useState('')
  const search = useDebounce(value)

  useEffect(() => {
    dispatch(setFilterDialogList(search))
  }, [search, dispatch, setFilterDialogList])

  return (
    <div className={styles.search}>
      <TextField
        placeholder='Search contact'
        className='override-inputSearch'
        fullWidth
        value={value}
        onChange={(event) => setValue(event.target.value)}
      />
    </div>
  )
}
